import { CommonComponentPresentation, CommonKVNode } from "../../../types";
import AddNodeRow from "./AddNodeRow";
import {
  CollapseChevron,
  KVKeyCell,
  KVMomentCell,
  KVValueCell,
  OpenNodeEditor,
} from "./KVStoreCells";
import {
  collapseChevronClass,
  CollapseState,
  deriveNodeCollapse,
  NodeDisplayMap,
  NodeFilters,
  toggledCollapseState,
} from "./kvStoreNodes";
import { NodeAdder } from "./useNodeAdding";
import { NodeEditor } from "./useNodeEditing";

// What every row of the tree shares: where and how it renders, the filters
// and the display map they produced, and the editors.
export type NodeTreeView = {
  componentPresentation?: CommonComponentPresentation;
  compact: boolean;
  delimiter: string | null;
  sessionId: string;
  agentId: string;
  isEditMode?: boolean;
  filters: NodeFilters;
  displayMap: NodeDisplayMap;
  setNodeCollapseState: (node: CommonKVNode, state: CollapseState) => void;
  editor: NodeEditor;
  adder: NodeAdder;
  onDeleteNode: (key: string) => void;
};

// One node's row, its add row when a child is being added under it, and
// its subtree as far as the collapse state shows.
const KVNodeView = ({
  view,
  k,
  fullKey,
  node,
  depth,
  ancestorMatches = false,
  forceDisplay = false,
}: {
  view: NodeTreeView;
  k: string;
  // The node's full delimiter-joined key: the editing identity, and what
  // the edit callbacks report.
  fullKey: string;
  node: CommonKVNode;
  depth: number;
  ancestorMatches?: boolean;
  // Render even a filtered-out node: a fully expanded parent shows all its
  // children.
  forceDisplay?: boolean;
}) => {
  const { filters, displayMap, editor, adder } = view;

  if (!(displayMap.get(node)?.displayNode ?? false) && !forceDisplay) {
    return null;
  }

  // Use presentation items to map moment_uuid in state to moment_id for links
  const item = view.componentPresentation?.items?.find(
    (item) => item.moment_uuid === node.moment_uuid
  );
  const hasValue = node.value !== undefined && node.value !== null;
  const childEntries = Object.entries(node.nodes ?? {});
  const hasChildNodes = childEntries.length > 0;

  const collapse = deriveNodeCollapse(
    k,
    node,
    filters,
    displayMap,
    ancestorMatches
  );
  const toggleCollapse = () => {
    if (collapse.isCollapsible) {
      view.setNodeCollapseState(node, toggledCollapseState(collapse));
    }
  };

  const openEditor: OpenNodeEditor | null =
    editor.editing && editor.editing.key === fullKey
      ? {
          state: editor.editing,
          keyInputRef: editor.keyInputRef,
          valueInputRef: editor.valueInputRef,
          onChange: editor.changeEditing,
          onApply: editor.applyEditing,
        }
      : null;

  const renderChildren = (childForceDisplay: boolean) =>
    childEntries.map(([childKey, childNode]) => (
      <KVNodeView
        key={childKey}
        view={view}
        k={childKey}
        fullKey={`${fullKey}${view.delimiter ?? "."}${childKey}`}
        node={childNode}
        depth={depth + 1}
        ancestorMatches={ancestorMatches || collapse.matchesFilters}
        forceDisplay={childForceDisplay}
      />
    ));

  return (
    <>
      <tr
        data-testid="kv-store-item"
        onClick={toggleCollapse}
        role={hasChildNodes && collapse.isCollapsible ? "button" : undefined}
      >
        <KVKeyCell
          depth={depth}
          segment={k}
          suffix={hasChildNodes ? (view.delimiter ?? "") : ""}
          tags={node.agency_tags}
          textFilter={filters.keyFilter}
          chevron={
            <CollapseChevron
              visible={hasChildNodes}
              className={collapseChevronClass(collapse)}
              disabled={!collapse.isCollapsible}
            />
          }
          isEditMode={view.isEditMode}
          editor={openEditor}
          onToggleCollapse={toggleCollapse}
          onStartEdit={() => editor.startEditing(fullKey, k, node, "key")}
        />
        <KVValueCell
          text={hasValue ? String(node.value) : null}
          textFilter={filters.valueFilter}
          compact={view.compact}
          isEditMode={view.isEditMode}
          editor={openEditor}
          onStartEdit={() => editor.startEditing(fullKey, k, node, "value")}
          onAddChild={() => adder.startAdding(fullKey)}
          onDelete={() => view.onDeleteNode(fullKey)}
        />
        <KVMomentCell
          item={item}
          hasValue={hasValue}
          isEditMode={view.isEditMode}
          sessionId={view.sessionId}
          agentId={view.agentId}
        />
      </tr>
      {adder.adding?.parentKey === fullKey && (
        <AddNodeRow depth={depth + 1} adder={adder} compact={view.compact} />
      )}
      {collapse.state === CollapseState.EXPANDED && renderChildren(true)}
      {collapse.state === CollapseState.MIXED && renderChildren(false)}
    </>
  );
};

export default KVNodeView;
